import { Component, OnInit } from '@angular/core';
import { IImport, ImportStatusEnum, ImportTypesEnum } from '../models/import';
import { translate } from '@jsverse/transloco';
import { BehaviorSubject, Observable, Subject, combineLatest, map, startWith, switchMap, tap, withLatestFrom } from 'rxjs';
import { select, Store } from '@ngrx/store';
import { AppStateInterface } from 'src/app/stores/appState.interface';
import { importsSelector } from 'src/app/stores/global/imports/import-selectors';
import { IBaseSearchRequest, IGridState, PageSize } from 'src/app/core/models/base';
import { GridDataResult, PageChangeEvent } from '@progress/kendo-angular-grid';
import * as ImportActions from "../../stores/global/imports/import-actions";
import { EmployeeService } from 'src/app/company/services/employee.service';
import { ImportFailReasons } from '../models/failed-import';
import { ImportService } from '../services/import-service';

@Component({
    selector: 'app-history-view',
    templateUrl: './history-view.component.html',
    styleUrl: './history-view.component.scss',
    standalone: false
})
export class HistoryViewComponent implements OnInit {

  // variables
  loading: boolean = false;


  constructor(
    private store: Store<AppStateInterface>,
    private employeeService: EmployeeService,
    private importService: ImportService
  ) {
  }

  //Getters
  get pageSizes(){
    return PageSize;
  }
  
  // (Behaviour)Subjects
  pageChangeSub$ = new Subject<PageChangeEvent>();
  gridState$ = new BehaviorSubject<IGridState>(
    {
      pageSize: this.pageSizes.Small,
      skip: 0,
    }
  );
  
  searchRequest$ = new BehaviorSubject<IBaseSearchRequest>({
    pageNumber: 0,
    pageSize: PageSize.Small,
  });

    // View Model
    vm$ = combineLatest([
      this.gridState$,
      this.searchRequest$.pipe(
        switchMap((request) => {
          this.loading = true;
          return this.importService.getImports(request)
        }),
        tap(() => {
          this.loading = false; // Stop loading when data is fetched
        }),
        map(x => {
          var gridData : GridDataResult = {
            data: x.data,
            total: x.totalCount
          }
          return gridData
        }),
        startWith(null)
      ),
      this.searchRequest$.pipe(startWith(null)),
      this.pageChangeSub$.pipe(
        withLatestFrom(this.searchRequest$),
          map(([pageChangeEvent, searchRequest]) => ({
            pageChangeEvent,
            searchRequest,
        })),
        tap(data => {
          const newPageSize = data.pageChangeEvent.take;
          const skip = data.searchRequest.pageSize !== newPageSize
            ? 0
            : data.pageChangeEvent.skip;
          const pageNumber = Math.round((skip + newPageSize) / newPageSize);
          this.gridState$.next({ skip, pageSize: newPageSize });
          this.searchRequest$.next({
            ...data.searchRequest,
            pageNumber: pageNumber,
            pageSize: newPageSize,
          });
        }),
        startWith({skip: 0, take: 10})
      )
    ]).pipe(
      map(([
        gridState,
        imports,
        searchRequest,
        pageChangeSub
      ]) => ({
        gridState,
        imports,
        searchRequest,
        pageChangeSub
      }))
    );


  ngOnInit() {
    this.store.dispatch(ImportActions.getImports());
  }

  getStatusString(status: ImportStatusEnum): string {
    switch (status) {
        case ImportStatusEnum.Pending:
            return translate('history.importStati.pending');
        case ImportStatusEnum.PartiallyImported:
            return translate('history.importStati.partiallyImported');
        case ImportStatusEnum.CompletelyImported:
          return translate('history.importStati.completelyImported');
        case ImportStatusEnum.NoneImported:
          return translate('history.importStati.noneImported');
        case ImportStatusEnum.Failed:
            return translate('history.importStati.failed');
        default:
            return translate('history.importStati.unknown');
    }
  }

  getTypeString(type: ImportTypesEnum): string {
    switch (type) {
        case ImportTypesEnum.Products:
            return translate('history.importTypes.products');
        case ImportTypesEnum.Devices:
            return translate('history.importTypes.devices');
        default:
            return translate('history.importTypes.unknown');
    }
  }

  getReasonString(reason: ImportFailReasons): string {
    switch (reason) {
        case ImportFailReasons.Unknown:
            return translate('history.failReasons.unknown');
        case ImportFailReasons.NameDuplicate:
            return translate('history.failReasons.nameDuplicate');
        case ImportFailReasons.ProductAndInternalNumberDuplicate:
          return translate('history.failReasons.productAndInternalNumberDuplicate');
        default:
            return translate('history.failReasons.unknown');
    }
  }

  public showDetailsCondition(dataItem: IImport): boolean {
    return (dataItem.failedImports.length > 0);
  }
}

